import { Component, type ReactElement, type ReactNode } from 'react';
import type { DashboardKpis } from '@/lib/dashboard';
import { useReadiness, type ReadinessLevel } from '@/lib/biometrics';
import { MetricTooltip } from '@/components/MetricTooltip';
import type { MetricKey } from '@/lib/metrics-glossary';

/**
 * Fila de chips compactos con KPIs accionables (M2.16).
 *
 * Decisiones:
 *  - Si `kpis` es `null`/`undefined`, NO renderiza nada (no muestra placeholders).
 *  - Cada chip se omite individualmente si su métrica viene null — un perfil
 *    nuevo sin nataciones todavía no debería ver "ACWR null".
 *  - Color por severidad calculado por umbral (verde/ámbar/rosa). Sigue la
 *    convención bg-{color}-50 + text-{color}-800 + border-{color}-200.
 *  - Tooltip nativo (`title`) con explicación breve, suficiente para que el
 *    usuario entienda qué significa la métrica sin abrir docs.
 *  - Layout: `flex flex-wrap gap-2` — se adapta a mobile sin overflow.
 */
export function KpiChipsRow({
  kpis,
}: {
  kpis: DashboardKpis | null | undefined;
}): ReactElement | null {
  if (!kpis) return null;

  const chips: ReactElement[] = [];

  if (kpis.monotony7d !== null) {
    chips.push(
      <Chip
        key="monotony"
        testId="kpi-chip-monotony"
        severity={monotonySeverity(kpis.monotony7d)}
        label={`Mon ${kpis.monotony7d.toFixed(1)}`}
        hint="Monotonía 7d (TSS medio / desv. estándar). >1.5 = poca variación, >2 = riesgo."
        metric="monotony"
      />,
    );
  }
  if (kpis.strainWeek !== null) {
    chips.push(
      <Chip
        key="strain"
        testId="kpi-chip-strain"
        severity={strainSeverity(kpis.strainWeek)}
        label={`Strain ${Math.round(kpis.strainWeek)}`}
        hint="Strain semanal (TSS×monotonía). >4000 elevado, >6000 muy elevado."
        metric="strain"
      />,
    );
  }
  if (kpis.ctlRamp7d !== null) {
    const v = kpis.ctlRamp7d;
    chips.push(
      <Chip
        key="ramp"
        testId="kpi-chip-ramp"
        severity={rampSeverity(v)}
        label={`Ramp ${formatSigned(v)}/sem`}
        hint="Cambio de CTL en los últimos 7 días. >+5 acelerando, >+8 riesgo lesión."
        metric="rampCtl"
      />,
    );
  }
  if (kpis.acwr !== null) {
    chips.push(
      <Chip
        key="acwr"
        testId="kpi-chip-acwr"
        severity={acwrSeverity(kpis.acwr)}
        label={`ACWR ${kpis.acwr.toFixed(1)}`}
        hint="Acute:Chronic Workload Ratio (TSS 7d / TSS 28d med). 0.8-1.3 óptimo."
        metric="acwr"
      />,
    );
  }
  if (kpis.compliance28d !== null) {
    const pct = Math.round(kpis.compliance28d * 100);
    chips.push(
      <Chip
        key="compliance"
        testId="kpi-chip-compliance"
        severity={complianceSeverity(pct)}
        label={`Plan ${pct}%`}
        hint="Cumplimiento del plan en 28d (TSS hecho / TSS planificado)."
        metric="compliance"
      />,
    );
  }
  chips.push(
    <Chip
      key="days-off"
      testId="kpi-chip-days-off"
      severity={daysOffSeverity(kpis.daysSinceLastOff)}
      label={`Sin off ${kpis.daysSinceLastOff}d`}
      hint="Días seguidos con TSS>0 (sin descanso completo). >7 vigila, >10 inserta off."
      metric="daysSinceOff"
    />,
  );
  if (kpis.nextARace) {
    chips.push(
      <Chip
        key="next-race"
        testId="kpi-chip-next-race"
        severity="info"
        label={`${kpis.nextARace.name} · ${kpis.nextARace.daysUntil}d`}
        hint={`Próxima carrera A: ${kpis.nextARace.name} (${kpis.nextARace.dateISO}).`}
        metric="nextARace"
      />,
    );
  }
  // M2.32.B — chip polarización (Z1-Z2 % del tiempo total, últimas 4 sem).
  if (kpis.polarizedRatio4w !== null) {
    const pct = Math.round(kpis.polarizedRatio4w * 100);
    chips.push(
      <Chip
        key="polarization"
        testId="kpi-chip-polarization"
        severity={polarizationSeverity(pct)}
        label={`Pol ${pct}%`}
        hint="Polarización 80/20 (Z1-Z2 % tiempo total, últ. 4 sem). ≥75% verde, ≥65% ámbar, <65% rosa."
        metric="polarization"
      />,
    );
  }
  // M2.32.D — chip balance de disciplinas. Siempre presente: "Balance OK"
  // si imbalanceWarn es null, "Balance · Swim 12d" si alguna disciplina
  // core lleva >7 días sin tocar.
  {
    const imb = kpis.imbalanceWarn;
    if (imb) {
      chips.push(
        <Chip
          key="balance"
          testId="kpi-chip-balance"
          severity={imb.days >= 14 ? 'danger' : 'warn'}
          label={`Balance · ${disciplineShortLabel(imb.discipline)} ${imb.days}d`}
          hint={`Disciplina sin tocar: ${disciplineShortLabel(imb.discipline)} hace ${imb.days} días. Reequilibra para evitar pérdida de adaptación.`}
          metric="disciplineBalance"
        />,
      );
    } else {
      chips.push(
        <Chip
          key="balance"
          testId="kpi-chip-balance"
          severity="ok"
          label="Balance OK"
          hint="Ninguna disciplina core (swim/bike/run) lleva >7 días sin tocar."
          metric="disciplineBalance"
        />,
      );
    }
  }

  // M2.32.A — chip readiness al final. Lo envolvemos en un error boundary
  // por si el componente se renderiza sin QueryClientProvider (tests
  // unitarios legacy) — en ese caso no añadimos nada y el resto del row
  // sigue funcionando.
  chips.push(
    <SafeReadinessChip key="readiness" />,
  );

  if (chips.length === 0) return null;

  return (
    <div
      data-testid="kpi-chips-row"
      aria-label="Indicadores clave de carga"
      className="flex flex-wrap items-center gap-2"
    >
      {chips}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chip + severity
// ---------------------------------------------------------------------------

type Severity = 'ok' | 'warn' | 'danger' | 'info';

function Chip({
  testId,
  severity,
  label,
  hint,
  metric,
}: {
  testId: string;
  severity: Severity;
  label: string;
  hint: string;
  /** Clave del glosario M2.32.E — añade un tooltip explicativo "?" inline. */
  metric?: MetricKey;
}): ReactElement {
  return (
    <span
      data-testid={testId}
      data-severity={severity}
      title={hint}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium tabular-nums ${chipClass(severity)}`}
    >
      {metric ? (
        <MetricTooltip metric={metric}>{label}</MetricTooltip>
      ) : (
        label
      )}
    </span>
  );
}

function chipClass(s: Severity): string {
  switch (s) {
    case 'ok':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    case 'warn':
      return 'border-amber-200 bg-amber-50 text-amber-800';
    case 'danger':
      return 'border-rose-200 bg-rose-50 text-rose-800';
    case 'info':
      return 'border-sky-200 bg-sky-50 text-sky-800';
  }
}

function formatSigned(n: number): string {
  const r = Math.round(n);
  return r >= 0 ? `+${r}` : `${r}`;
}

// ---------------------------------------------------------------------------
// Severity rules (alineadas con contrato M2.16)
// ---------------------------------------------------------------------------

export function monotonySeverity(v: number): Severity {
  if (v >= 2.0) return 'danger';
  if (v >= 1.5) return 'warn';
  return 'ok';
}

export function strainSeverity(v: number): Severity {
  if (v >= 6000) return 'danger';
  if (v >= 4000) return 'warn';
  return 'ok';
}

export function rampSeverity(v: number): Severity {
  const abs = Math.abs(v);
  if (abs >= 8) return 'danger';
  if (abs >= 5) return 'warn';
  return 'ok';
}

export function acwrSeverity(v: number): Severity {
  if (v > 1.5 || v < 0.8) return 'danger';
  if (v > 1.3) return 'warn';
  return 'ok';
}

export function complianceSeverity(pct: number): Severity {
  if (pct >= 80 && pct <= 110) return 'ok';
  if ((pct >= 70 && pct < 80) || (pct > 110 && pct <= 120)) return 'warn';
  return 'danger';
}

export function daysOffSeverity(days: number): Severity {
  if (days >= 10) return 'danger';
  if (days >= 7) return 'warn';
  return 'ok';
}

// ---------------------------------------------------------------------------
// M2.32.B/D — helpers añadidos para los nuevos chips Polarización y Balance.
// Standalone, no mutan severidad existente.
// ---------------------------------------------------------------------------

/** Severity del chip de polarización por porcentaje Z1-Z2 / total. */
export function polarizationSeverity(pct: number): Severity {
  if (pct >= 75) return 'ok';
  if (pct >= 65) return 'warn';
  return 'danger';
}

/** Etiqueta corta de la disciplina para el chip de balance. */
function disciplineShortLabel(d: 'swim' | 'bike' | 'run'): string {
  switch (d) {
    case 'swim':
      return 'Swim';
    case 'bike':
      return 'Bike';
    case 'run':
      return 'Run';
  }
}

// ---------------------------------------------------------------------------
// M2.32.A — Readiness chip
// ---------------------------------------------------------------------------

function readinessSeverity(level: ReadinessLevel): Severity {
  switch (level) {
    case 'green':
      return 'ok';
    case 'yellow':
      return 'warn';
    case 'red':
      return 'danger';
  }
}

/**
 * Chip que lee `useReadiness()`. Si todavía no hay datos / no hay baseline,
 * devuelve null (no añade nada al row).
 */
function ReadinessChipFromQuery(): ReactElement | null {
  const query = useReadiness();
  if (query.isLoading || query.isError) return null;
  const data = query.data;
  if (!data || !data.available) return null;
  const severity = readinessSeverity(data.level);
  return (
    <Chip
      testId="kpi-chip-readiness"
      severity={severity}
      label={`Readiness ${data.score}`}
      hint={`Readiness biométrico (HRV/RHR/sueño) → ${data.score}/100 nivel ${data.level}.`}
      metric="readiness"
    />
  );
}

/**
 * Wrapper defensivo: si el componente se renderiza fuera de un
 * QueryClientProvider (tests legacy de KpiChipsRow), capturamos el error y
 * dejamos el chip fuera del row sin afectar al resto.
 */
class ReadinessErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  override state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  override componentDidCatch() {
    // Silenciamos — la ausencia del chip ya es señal suficiente.
  }
  override render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export function SafeReadinessChip(): ReactElement {
  return (
    <ReadinessErrorBoundary>
      <ReadinessChipFromQuery />
    </ReadinessErrorBoundary>
  );
}
